import React, { useEffect, useState } from 'react';
import { useDashboard, useFormatage } from '../hooks/useAgroshopHooks';
import { useSearchParams, useNavigate } from 'react-router-dom';
import StockPanel from './StockPanel';
import UserPanel from './UserPanel';
import AddProductPanel from './AddProductPanel';

export default function AdminDashboard() {
  const { stats, loading, erreur, derniereMAJ, recharger } = useDashboard();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [searchParams] = useSearchParams();
  const panel = searchParams.get('panel');
  const [showStock, setShowStock] = useState(true);
  const [showUsers, setShowUsers] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    setDashboardStats(stats);
  }, [stats]);

  React.useEffect(() => {
    if (panel === 'stock') {
      setShowStock(true);
      setShowUsers(false);
      setShowAddProduct(false);
    } else if (panel === 'users') {
      setShowStock(false);
      setShowUsers(true);
      setShowAddProduct(false);
    } else if (panel === 'add') {
      setShowStock(false);
      setShowUsers(false);
      setShowAddProduct(true);
    } else {
      setShowStock(true);
      setShowUsers(true);
      setShowAddProduct(false);
    }
  }, [panel]);

  const onlySelectedPanel = ['stock', 'users', 'add'].includes(panel);
  const { formatPrix, formatDate } = useFormatage();

  if (loading) return <div className="ag-container">Chargement de l'espace administrateur…</div>;
  if (erreur) return <div className="ag-container">Erreur : {erreur}</div>;
  if (!dashboardStats) return null;

  const stockMax = Math.max(1, ...((dashboardStats?.produits_en_stock || []).map((produit) => produit.stock_disponible)));

  return (
    <div className="ag-admin-shell">
      <aside className="ag-admin-sidebar">
        <div className="ag-admin-brand">
          <div className="ag-admin-brand-icon">🌿</div>
          <div>
            <strong>AgriShop</strong>
            <p>Tableau de bord admin</p>
          </div>
        </div>

        <nav className="ag-admin-sidebar-nav">
          <button type="button" className={`ag-admin-sidebar-link ${!panel ? 'active' : ''}`} onClick={() => navigate('/admin')}>Accueil</button>
          <button type="button" className={`ag-admin-sidebar-link ${panel === 'stock' ? 'active' : ''}`} onClick={() => navigate('/admin?panel=stock')}>Produits</button>
          <button type="button" className={`ag-admin-sidebar-link ${panel === 'users' ? 'active' : ''}`} onClick={() => navigate('/admin?panel=users')}>Clients</button>
          <button type="button" className={`ag-admin-sidebar-link ${panel === 'stats' ? 'active' : ''}`} onClick={() => navigate('/admin?panel=stats')}>Stats</button>
          <button type="button" className={`ag-admin-sidebar-link ${panel === 'add' ? 'active' : ''}`} onClick={() => navigate('/admin?panel=add')}>Paramètres</button>
        </nav>

        <div className="ag-admin-sidebar-footer">
          <span>Dernière mise à jour</span>
          <strong>{formatDate(derniereMAJ?.toISOString?.() || new Date().toISOString())}</strong>
        </div>
      </aside>

      <main className="ag-admin-main">
        <div className="ag-card ag-card--admin">
          <h2>Tableau de bord</h2>
          <p>Vue centrale pour l'administrateur : ventes, commandes, utilisateurs et niveaux de stock.</p>

          {!onlySelectedPanel && (
        <>
          <div className="ag-dashboard-header-row">
            <div>
              <h3 className="ag-dashboard-section-title">Indicateurs principaux</h3>
              <p className="ag-muted">Vue synthétique des KPI de vente, client et stock.</p>
            </div>
          </div>

          <div className="ag-dashboard-grid ag-dashboard-grid--metrics">
            <div className="ag-card-tile ag-card-tile--green">
              <div className="ag-card-label"><span className="ag-tile-icon">💸</span> Ventes du mois</div>
              <div>
                <strong className="ag-card-value">{formatPrix(stats.ventes_mois)}</strong>
                <div className="ag-card-sub">Chiffre net ce mois</div>
              </div>
            </div>
            <div className="ag-card-tile ag-card-tile--dark">
              <div className="ag-card-label"><span className="ag-tile-icon">📦</span> Commandes</div>
              <div>
                <strong className="ag-card-value">{stats.nb_commandes_mois}</strong>
                <div className="ag-card-sub">Commandes réglées ce mois</div>
              </div>
            </div>
            <div className="ag-card-tile ag-card-tile--blue">
              <div className="ag-card-label"><span className="ag-tile-icon">🧑‍🤝‍🧑</span> Clients actifs</div>
              <div>
                <strong className="ag-card-value">{stats.nb_clients_actifs}</strong>
                <div className="ag-card-sub">Sur les 30 derniers jours</div>
              </div>
            </div>
            <div className="ag-card-tile ag-card-tile--orange">
              <div className="ag-card-label"><span className="ag-tile-icon">🛒</span> Vendeurs</div>
              <div>
                <strong className="ag-card-value">{stats.nb_vendeurs}</strong>
                <div className="ag-card-sub">Commerciaux actifs</div>
              </div>
            </div>
            <div className="ag-card-tile ag-card-tile--gray">
              <div className="ag-card-label"><span className="ag-tile-icon">📈</span> Produits en stock</div>
              <div>
                <strong className="ag-card-value">{stats.produits_en_stock.length}</strong>
                <div className="ag-card-sub">Articles disponibles</div>
              </div>
            </div>
          </div>

          <div className="ag-dashboard-grid ag-dashboard-grid--metrics" style={{ marginTop: '18px' }}>
            <div className="ag-card-tile ag-card-tile--purple">
              <div className="ag-card-label"><span className="ag-tile-icon">📊</span> Conversion</div>
              <div>
                <strong className="ag-card-value">{stats.taux_conversion_mensuel ?? 0}%</strong>
                <div className="ag-card-sub">Taux de commandes payées sur ce mois</div>
              </div>
            </div>
            <div className="ag-card-tile ag-card-tile--teal">
              <div className="ag-card-label"><span className="ag-tile-icon">💰</span> Panier moyen</div>
              <div>
                <strong className="ag-card-value">{formatPrix(stats.moyenne_commande_mensuelle)}</strong>
                <div className="ag-card-sub">Valeur moyenne des commandes payées</div>
              </div>
            </div>
            <div className="ag-card-tile ag-card-tile--yellow">
              <div className="ag-card-label"><span className="ag-tile-icon">📆</span> Ventes semaine</div>
              <div>
                <strong className="ag-card-value">{formatPrix(stats.ventes_semaine)}</strong>
                <div className="ag-card-sub">Chiffre net 7 derniers jours</div>
              </div>
            </div>
            <div className="ag-card-tile ag-card-tile--brown">
              <div className="ag-card-label"><span className="ag-tile-icon">👥</span> Clients totaux</div>
              <div>
                <strong className="ag-card-value">{stats.nb_clients_total}</strong>
                <div className="ag-card-sub">Clients enregistrés</div>
              </div>
            </div>
          </div>

          <section className="ag-dashboard-summary-section">
            <header className="ag-dashboard-panel-header">
              <div>
                <h3>Données de vente calculées</h3>
                <p className="ag-muted">Répartition réelle des ventes par catégorie, région et moyen de paiement.</p>
              </div>
            </header>
            <div className="ag-dashboard-summary-grid">
              <article className="ag-summary-card">
                <div className="ag-summary-card-title">Ventes par catégorie</div>
                {dashboardStats.ventes_par_categorie?.length ? (
                  <ul className="ag-summary-data">
                    {dashboardStats.ventes_par_categorie.slice(0, 5).map((item) => (
                      <li key={item.categorie}>
                        <span>{item.categorie}</span>
                        <strong>{formatPrix(item.chiffre)}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Aucune vente de catégorie enregistrée ce mois.</p>
                )}
              </article>

              <article className="ag-summary-card">
                <div className="ag-summary-card-title">Paiements reçus</div>
                {dashboardStats.paiements_par_operateur?.length ? (
                  <ul className="ag-summary-data">
                    {dashboardStats.paiements_par_operateur.map((item) => (
                      <li key={item.operateur}>
                        <span>{item.operateur_display}</span>
                        <strong>{formatPrix(item.montant_total)}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Aucun paiement confirmé ce mois.</p>
                )}
              </article>

              <article className="ag-summary-card ag-summary-card--highlight">
                <div className="ag-summary-card-title">Segmentation clients</div>
                {dashboardStats.segmentation_clients?.length ? (
                  <ul className="ag-summary-data">
                    {dashboardStats.segmentation_clients.map((item) => (
                      <li key={item.statut}>
                        <span>{item.statut_display}</span>
                        <strong>{item.count}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Aucun client enregistré.</p>
                )}
              </article>

              <article className="ag-summary-card">
                <div className="ag-summary-card-title">Top clients</div>
                {dashboardStats.top_clients?.length ? (
                  <ul className="ag-summary-data">
                    {dashboardStats.top_clients.slice(0, 5).map((client) => (
                      <li key={client.client_id}>
                        <span>{client.nom_complet || client.telephone}</span>
                        <strong>{formatPrix(client.total_achete)}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Aucun client prioritaire ce mois.</p>
                )}
              </article>

              <article className="ag-summary-card">
                <div className="ag-summary-card-title">Top région</div>
                {dashboardStats.ventes_par_region?.[0] ? (
                  <div className="ag-summary-item">
                    <span>{dashboardStats.ventes_par_region[0].region}</span>
                    <strong>{formatPrix(dashboardStats.ventes_par_region[0].chiffre)}</strong>
                  </div>
                ) : (
                  <p>Aucune région dominante ce mois.</p>
                )}
              </article>
            </div>
          </section>

          <section className="ag-dashboard-advice">
            <header className="ag-dashboard-panel-header">
              <div>
                <h3>Conseils métiers</h3>
                <p className="ag-muted">Recommandations basées sur les indicateurs et les tendances observées.</p>
              </div>
            </header>
            <div className="ag-dashboard-summary-grid ag-dashboard-summary-grid--advice">
              <article className="ag-summary-card">
                <div className="ag-summary-card-title">Surveillez le stock</div>
                <div className="ag-summary-item">
                  <span>Produits bas</span>
                  <strong>{stats.produits_stock_faible}</strong>
                </div>
                <div className="ag-summary-item">
                  <span>Ruptures</span>
                  <strong>{stats.produits_rupture}</strong>
                </div>
                <div className="ag-summary-note">Priorisez le réapprovisionnement des articles les plus vendus pour éviter les ruptures.</div>
              </article>

              <article className="ag-summary-card">
                <div className="ag-summary-card-title">Focus commercial</div>
                {dashboardStats.ventes_par_categorie?.[0] ? (
                  <>
                    <div className="ag-summary-item">
                      <span>Catégorie en tête</span>
                      <strong>{dashboardStats.ventes_par_categorie[0].categorie}</strong>
                    </div>
                    <div className="ag-summary-note">Renforcez l'offre et la visibilité sur cette catégorie pour tirer parti de sa performance.</div>
                  </>
                ) : (
                  <p>Pas encore de catégorie dominante.</p>
                )}
              </article>

              <article className="ag-summary-card">
                <div className="ag-summary-card-title">Région prioritaire</div>
                {dashboardStats.ventes_par_region?.[0] ? (
                  <>
                    <div className="ag-summary-item">
                      <span>Région la plus active</span>
                      <strong>{dashboardStats.ventes_par_region[0].region}</strong>
                    </div>
                    <div className="ag-summary-note">Renforcez les stocks et les opérations logistiques dans cette région.</div>
                  </>
                ) : (
                  <p>Pas de région dominante ce mois.</p>
                )}
              </article>
            </div>
          </section>

          <section className="ag-dashboard-section">
            <header className="ag-dashboard-panel-header">
              <div>
                <h3>Stock et utilisateurs</h3>
                <p className="ag-muted">Niveaux de stock et utilisateurs récents côte à côte.</p>
              </div>
            </header>
            <div className="ag-dashboard-columns">
              <StockPanel produits_en_stock={dashboardStats.produits_en_stock || []} />
              <UserPanel
                vendeurs_recents={dashboardStats.vendeurs_recents || []}
                clients_recents={dashboardStats.clients_recents || []}
                onChange={recharger}
              />
            </div>
          </section>
        </>
      )}

      {onlySelectedPanel ? (
        <div className="ag-dashboard-columns">
          <section className="ag-dashboard-panel ag-dashboard-panel--wide">
            <div className="ag-dashboard-panel-header" style={{justifyContent: 'space-between'}}>
              <div>
                <h3>{panel === 'stock' ? 'Stock' : panel === 'users' ? 'Utilisateurs' : 'Ajouter produit'}</h3>
                <p className="ag-muted">Affichage dédié au panneau sélectionné.</p>
              </div>
              <button className="ag-btn-secondary" type="button" onClick={() => navigate('/admin')}>Retour au tableau</button>
            </div>
            {panel === 'stock' && <StockPanel produits_en_stock={dashboardStats.produits_en_stock || []} />}
            {panel === 'users' && (
              <UserPanel
                vendeurs_recents={dashboardStats.vendeurs_recents || []}
                clients_recents={dashboardStats.clients_recents || []}
                onChange={recharger}
              />
            )}
            {panel === 'add' && <AddProductPanel onCreated={() => window.location.reload()} />}
          </section>
        </div>
      ) : (
        <div className="ag-dashboard-columns">
          <section className="ag-dashboard-panel ag-dashboard-panel--wide">
            <div className="ag-dashboard-panel-header">
              <div>
                <h3>Dernières commandes</h3>
                <p className="ag-muted">{stats.commandes_recentes.length} commandes récentes</p>
              </div>
            </div>
            <div className="ag-dashboard-table-wrapper">
              <table className="ag-dashboard-table">
                <thead>
                  <tr>
                    <th>Commande</th>
                    <th>Client</th>
                    <th>Statut</th>
                    <th>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.commandes_recentes.map((commande) => (
                    <tr key={commande.id}>
                      <td>{commande.numero}</td>
                      <td>{commande.client_nom || commande.client_telephone || 'Anonyme'}</td>
                      <td>
                        <span className={`ag-badge ag-badge--status ag-badge--${commande.statut === 'livree' ? 'success' : commande.statut === 'annule' ? 'danger' : 'warning'}`}>
                          {commande.statut_display || commande.statut}
                        </span>
                      </td>
                      <td>{formatPrix(commande.montant_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
      <footer className="ag-muted">Dernière mise à jour : {formatDate(derniereMAJ?.toISOString?.() || new Date().toISOString())}</footer>
        </div>
      </main>
    </div>
  );
}
